/**
 * 分镜 prompt 模板快速插入栏
 *
 * 形态：横排 N 个折叠组按钮（[景别 ▾]、[镜头 ▾]、[节奏 ▾]…），点击展开本组的胶囊。
 * 选中胶囊时通过 `onInsert(text)` 回调把片段透传给上层（上层调 MentionTextarea
 * 的命令式 `insertText` 完成"插到光标位置 + 自动连接符"）。
 *
 * 设计要点：
 * - 同时只展开一个组；点击同一个组按钮再次关闭
 * - 不存任何状态到 DB / localStorage（纯瞬态）
 * - 不区分单图 / 多图 / 帧序列，所有写分镜 prompt 的地方都能用同一个组件
 * - 视觉与 ShotInspector 的小标题对齐：sky 系底色 + slate 文字
 */

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Sparkles } from 'lucide-react';
import { directorApi } from '../../services/directorApi';
import {
  PROMPT_TEMPLATE_GROUPS,
  normalizePromptTemplateGroups,
  type PromptTemplateGroup,
} from '../../utils/promptTemplates';

interface Props {
  /** 用户点击某个模板胶囊后的回调，参数为完整片段文本 */
  onInsert: (text: string) => void;
  /** 自定义模板组（默认用全集 PROMPT_TEMPLATE_GROUPS） */
  groups?: PromptTemplateGroup[];
  /** 整体禁用（视频已激活时禁止改 prompt） */
  disabled?: boolean;
  className?: string;
}

export function PromptTemplateBar({
  onInsert,
  groups,
  disabled = false,
  className = '',
}: Props) {
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [remoteGroups, setRemoteGroups] = useState<PromptTemplateGroup[]>(PROMPT_TEMPLATE_GROUPS);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (groups) return;
    let cancelled = false;
    directorApi
      .getPublicSettings()
      .then((settings) => {
        if (cancelled) return;
        setRemoteGroups(normalizePromptTemplateGroups(settings.promptTemplateGroups));
      })
      .catch(() => {
        if (cancelled) return;
        setRemoteGroups(PROMPT_TEMPLATE_GROUPS);
      });
    return () => {
      cancelled = true;
    };
  }, [groups]);

  // 点击组件外部时关闭展开的组
  useEffect(() => {
    if (!openGroup) return;
    const onDoc = (e: MouseEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return;
      setOpenGroup(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [openGroup]);

  if (disabled) return null;

  const resolvedGroups = groups ?? remoteGroups;
  const current = resolvedGroups.find((g) => g.groupLabel === openGroup);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <span className="inline-flex shrink-0 items-center gap-1 text-[10px] font-medium uppercase tracking-[0.18em] text-slate-400">
          <Sparkles className="h-3 w-3 text-sky-400" />
          模板
        </span>
        {resolvedGroups.map((g) => {
          const open = openGroup === g.groupLabel;
          return (
            <button
              key={g.groupLabel}
              type="button"
              onClick={() => setOpenGroup(open ? null : g.groupLabel)}
              className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition ${
                open
                  ? 'bg-sky-100 text-sky-700 ring-1 ring-inset ring-sky-200'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {g.groupLabel}
              <ChevronDown
                className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`}
              />
            </button>
          );
        })}
      </div>

      {/* 展开面板 */}
      {current && (
        <div className="mt-1.5 rounded-2xl border border-slate-200 bg-white p-2 shadow-md shadow-slate-200/40">
          {current.groupHint && (
            <p className="mb-1.5 px-1 text-[10px] leading-4 text-slate-400">
              {current.groupHint}
            </p>
          )}
          <div className="flex flex-wrap gap-1.5">
            {current.templates.map((t) => (
              <button
                key={t.label}
                type="button"
                onMouseDown={(e) => {
                  // mouseDown + preventDefault：避免输入框失去焦点丢失光标位置
                  e.preventDefault();
                  onInsert(t.text);
                  setOpenGroup(null);
                }}
                className="inline-flex shrink-0 items-center rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-medium text-sky-700 ring-1 ring-inset ring-sky-100 transition hover:bg-sky-100"
                title={t.text}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
